/**
 * Profile → Settings → "Delete account" (self-service, launch audit). Two
 * steps: open the panel, type DELETE, confirm. The deleteMyAccount callable
 * cancels any subscription immediately, deletes the account and its data
 * (functions/account/deleteAccount.js), then the user is signed out.
 * Admin accounts are deleted by another admin from Admin → Users.
 */
import React, { useState } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

export async function requestAccountDeletion(call = (data) => httpsCallable(getFunctions(getApp()), 'deleteMyAccount', { timeout: 120000 })(data)) {
  const res = await call({ confirm: 'DELETE' });
  return res?.data || {};
}

export default function DeleteAccountSection({ isAdmin, hasSubscription, onDeleted, request = requestAccountDeletion }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (isAdmin) {
    return <Text style={styles.note}>Admin accounts are deleted by another admin from Admin → Users.</Text>;
  }
  const ready = typed.trim() === 'DELETE';

  const confirm = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError('');
    try {
      await request();
      onDeleted?.();
    } catch (e) {
      setError(e?.message ? e.message.replace(/^.*?:\s*/, '') : 'Could not delete your account. Try again or contact contact@betoptimized.com.');
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Pressable onPress={() => setOpen(true)} accessibilityRole="button" style={({ hovered }) => [styles.row, hovered && styles.rowHover]} testID="delete-account-open">
        <Ionicons name="trash-outline" size={16} color="#f87171" />
        <Text style={styles.rowText}>Delete account</Text>
      </Pressable>
    );
  }
  return (
    <View style={styles.panel} testID="delete-account-panel">
      <Text style={styles.title}>Delete your account permanently?</Text>
      <Text style={styles.body}>
        {hasSubscription ? 'Your subscription is cancelled right away and will not renew; the current period is not refunded. ' : ''}
        Your profile, search history, saved searches, bet slips and uploaded images are deleted. This can't be undone.
      </Text>
      <Text style={styles.label} nativeID="delete-confirm-label">Type DELETE to confirm</Text>
      <TextInput
        value={typed}
        onChangeText={setTyped}
        autoCapitalize="characters"
        autoCorrect={false}
        editable={!busy}
        placeholder="DELETE"
        placeholderTextColor="rgba(255,255,255,0.25)"
        accessibilityLabelledBy="delete-confirm-label"
        aria-labelledby="delete-confirm-label"
        style={styles.input}
        testID="delete-account-input"
      />
      {error ? <Text style={styles.error} accessibilityRole="alert">{error}</Text> : null}
      <View style={styles.actions}>
        <Pressable onPress={() => { setOpen(false); setTyped(''); setError(''); }} disabled={busy} accessibilityRole="button" style={styles.cancel}>
          <Text style={styles.cancelText}>Keep my account</Text>
        </Pressable>
        <Pressable onPress={confirm} disabled={!ready || busy} accessibilityRole="button" accessibilityState={{ disabled: !ready || busy }} style={[styles.danger, (!ready || busy) && styles.dangerOff]} testID="delete-account-confirm">
          <Text style={styles.dangerText}>{busy ? 'Deleting…' : 'Delete account'}</Text>
        </Pressable>
      </View>
    </View>
  );
}

const pointer = Platform.OS === 'web' ? { cursor: 'pointer' } : {};
const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 12, paddingHorizontal: 4, borderRadius: 8, ...pointer },
  rowHover: { backgroundColor: 'rgba(248,113,113,0.08)' },
  rowText: { color: '#f87171', fontSize: 14, fontWeight: '500' },
  note: { color: 'rgba(255,255,255,0.4)', fontSize: 12, paddingVertical: 10 },
  panel: { marginTop: 8, padding: 16, borderRadius: 12, gap: 10, borderWidth: 1, borderColor: 'rgba(248,113,113,0.35)', backgroundColor: 'rgba(248,113,113,0.06)' },
  title: { color: '#fff', fontSize: 15, fontWeight: '600' },
  body: { color: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: 19 },
  label: { color: 'rgba(255,255,255,0.6)', fontSize: 12 },
  input: { borderWidth: 1, borderColor: 'rgba(255,255,255,0.2)', borderRadius: 8, paddingVertical: 9, paddingHorizontal: 12, color: '#fff', fontSize: 14 },
  error: { color: '#F87171', fontSize: 13 },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' },
  cancel: { paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)', ...pointer },
  cancelText: { color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: '500' },
  danger: { paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8, backgroundColor: '#DC2626', ...pointer },
  dangerOff: { opacity: 0.45 },
  dangerText: { color: '#fff', fontSize: 13, fontWeight: '600' },
});
